"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, ComposedChart, Line,
} from "recharts";
import { RefreshCcw, Boxes, Clock, Gauge, BarChart3 } from "lucide-react";

/* ── 차트 색상 (다크모드에서도 잘 보이는 값) ── */
const C = {
  primary:  "#818cf8",
  success:  "#34d399",
  warning:  "#fbbf24",
  accent:   "#a78bfa",
  danger:   "#f87171",
  cyan:     "#22d3ee",
  pink:     "#f472b6",
  lime:     "#a3e635",
};
const PIE_COLORS = [C.primary, C.success, C.warning, C.accent, C.danger, C.cyan, C.pink, C.lime];

/* ── 타입 ── */
interface BlockActivity   { date: string; block_count: string; tx_count: string; }
interface BlockTimeline    { block_num: number; tx_count: number; created_at: string; interval_sec: string | number | null; }
interface TxByOrg          { name: string; value: string; }
interface ChaincodeActivity { chaincode_name: string; fn: string; call_count: string; }
interface HeatmapRow       { date: string; hour: number; count: string; }
interface HeatmapChaincodeRow { date: string; hour: number; chaincode_name: string; count: string; }
interface HeatmapBlockRow  { date: string; hour: number; block_count: string; }
interface HeatmapMspRow    { date: string; hour: number; msp: string; count: string; }
interface BlockSizeDist    { tx_count: number; block_count: string; }

/* ── 유틸 ── */
function fmtSecLong(sec: number) {
  if (sec < 1) return `${(sec * 1000).toFixed(0)} 밀리초`;
  if (sec < 60) return `${sec.toFixed(1)} 초`;
  if (sec < 3600) return `${(sec / 60).toFixed(1)} 분`;
  return `${(sec / 3600).toFixed(1)} 시간`;
}

/* ── Tooltip 래퍼 ── */
const tipBox = "bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl px-3 py-2 shadow-xl text-xs";
function TipTimeline({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const iv = payload.find((p: any) => p.dataKey === "interval");
  const tx = payload.find((p: any) => p.dataKey === "txs");
  const tps = iv && tx && iv.value > 0 ? tx.value / iv.value : null;
  const isActive = iv && iv.value <= 60;
  return (
    <div className={tipBox}>
      <p className="font-semibold text-[var(--color-card-foreground)] mb-1">블록 {label}</p>
      {tx && <p style={{ color: C.success }}>포함 TX: {tx.value}건</p>}
      {iv && (
        <p style={{ color: C.primary }}>
          {isActive ? "블록 생성 주기" : "이전 블록 이후 경과"}: {fmtSecLong(iv.value)}
        </p>
      )}
      {isActive && tps !== null && (
        <p style={{ color: C.warning }}>
          TPS: {tps >= 100 ? Math.round(tps) : tps >= 1 ? tps.toFixed(1) : tps.toFixed(3)}
        </p>
      )}
      {!isActive && iv && (
        <p className="text-[var(--color-muted-foreground)] italic">네트워크 유휴 구간</p>
      )}
    </div>
  );
}
function TipActivity({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className={tipBox}>
      <p className="font-semibold text-[var(--color-card-foreground)] mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.stroke || p.fill }}>{p.name}: {Number(p.value).toLocaleString()}</p>
      ))}
    </div>
  );
}
function TipOrg({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className={tipBox}>
      <p className="font-semibold text-[var(--color-card-foreground)]">{d.name}</p>
      <p style={{ color: d.payload?.fill }}>{Number(d.value).toLocaleString()} 트랜잭션</p>
    </div>
  );
}
function TipGeneric({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className={tipBox}>
      <p className="font-semibold text-[var(--color-card-foreground)] mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.fill || p.stroke }}>{p.name}: {Number(p.value).toLocaleString()}</p>
      ))}
    </div>
  );
}

/* ── 히트맵 ── */
function HeatmapGrid({ data, chaincodeData, blockData, mspData }: {
  data: HeatmapRow[];
  chaincodeData?: HeatmapChaincodeRow[];
  blockData?: HeatmapBlockRow[];
  mspData?: HeatmapMspRow[];
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    fixedX: number; fixedY: number; date: string; hour: number; count: number;
    chaincodes: { name: string; count: number }[];
    blockCount: number;
    msps: { name: string; count: number }[];
    avgRatio: number;
    isDailyPeak: boolean;
    txPerBlock: number | null;
    prevDayCount: number | null;
    hourAvg: number;
    dailyTotal: number;
    dailyShare: number;
  } | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // 추가 데이터 맵 생성
  const ccMap = useMemo(() => {
    const m = new Map<string, { name: string; count: number }[]>();
    if (!chaincodeData) return m;
    chaincodeData.forEach(d => {
      const key = `${d.date}_${d.hour}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push({ name: d.chaincode_name, count: parseInt(d.count) });
    });
    return m;
  }, [chaincodeData]);

  const blkMap = useMemo(() => {
    const m = new Map<string, number>();
    if (!blockData) return m;
    blockData.forEach(d => { m.set(`${d.date}_${d.hour}`, parseInt(d.block_count)); });
    return m;
  }, [blockData]);

  const mspMap = useMemo(() => {
    const m = new Map<string, { name: string; count: number }[]>();
    if (!mspData) return m;
    mspData.forEach(d => {
      const key = `${d.date}_${d.hour}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push({ name: d.msp, count: parseInt(d.count) });
    });
    return m;
  }, [mspData]);

  const grid = useMemo(() => {
    const map = new Map<string, number>();
    let maxVal = 1;
    let totalAll = 0;
    let cellCount = 0;
    data.forEach(d => {
      const key = `${d.date}_${d.hour}`;
      const v = parseInt(d.count);
      map.set(key, v);
      if (v > maxVal) maxVal = v;
      totalAll += v;
      cellCount++;
    });
    const dates = [...new Set(data.map(d => d.date))].sort();
    const totalByHour = new Map<number, number>();
    const countByHour = new Map<number, number>();
    data.forEach(d => {
      const h = d.hour;
      totalByHour.set(h, (totalByHour.get(h) || 0) + parseInt(d.count));
      countByHour.set(h, (countByHour.get(h) || 0) + 1);
    });
    const peakHour = [...totalByHour.entries()].sort((a, b) => b[1] - a[1])[0];

    // 일별 총 TX
    const totalByDate = new Map<string, number>();
    data.forEach(d => {
      totalByDate.set(d.date, (totalByDate.get(d.date) || 0) + parseInt(d.count));
    });

    // 일별 피크 시간
    const peakByDate = new Map<string, { hour: number; count: number }>();
    data.forEach(d => {
      const v = parseInt(d.count);
      const cur = peakByDate.get(d.date);
      if (!cur || v > cur.count) peakByDate.set(d.date, { hour: d.hour, count: v });
    });

    const avgPerCell = cellCount > 0 ? totalAll / cellCount : 0;

    // 시간대별 7일 평균
    const avgByHour = new Map<number, number>();
    for (const [h, total] of totalByHour.entries()) {
      avgByHour.set(h, total / (countByHour.get(h) || 1));
    }

    return { map, dates, maxVal, peakHour, avgPerCell, totalByDate, peakByDate, avgByHour };
  }, [data]);

  if (grid.dates.length === 0) return <Empty />;

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const handleMouseEnter = (e: React.MouseEvent, date: string, hour: number, count: number) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const key = `${date}_${hour}`;

    // 뷰포트 기반 fixed 좌표: 셀의 중앙 상단 위에 표시
    const fixedX = rect.left + rect.width / 2;
    const fixedY = rect.top - 8;

    // 추가 통계 계산
    const avgRatio = grid.avgPerCell > 0 ? count / grid.avgPerCell : 0;
    const dailyPeak = grid.peakByDate.get(date);
    const isDailyPeak = dailyPeak ? dailyPeak.hour === hour && count > 0 : false;
    const blkCount = blkMap.get(key) || 0;
    const txPerBlock = blkCount > 0 ? count / blkCount : null;

    const dateObj = new Date(date);
    dateObj.setDate(dateObj.getDate() - 1);
    const prevDateStr = dateObj.toISOString().split("T")[0];
    const prevKey = `${prevDateStr}_${hour}`;
    const prevDayCount = grid.map.has(prevKey) ? (grid.map.get(prevKey) ?? null) : null;

    const hourAvg = grid.avgByHour.get(hour) || 0;
    const dailyTotal = grid.totalByDate.get(date) || 0;
    const dailyShare = dailyTotal > 0 ? (count / dailyTotal) * 100 : 0;

    setTooltip({
      fixedX, fixedY,
      date, hour, count,
      chaincodes: ccMap.get(key) || [],
      blockCount: blkCount,
      msps: mspMap.get(key) || [],
      avgRatio, isDailyPeak, txPerBlock, prevDayCount, hourAvg, dailyTotal, dailyShare,
    });
  };

  // 툴팁을 Portal로 body에 직접 렌더링 (모든 overflow 무시)
  const tooltipPortal = mounted && tooltip && createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: tooltip.fixedX,
        top: tooltip.fixedY,
        transform: "translate(-50%, -100%)",
      }}
    >
      <div className={`${tipBox} whitespace-nowrap min-w-[180px]`}>
        <p className="font-semibold text-[var(--color-card-foreground)] mb-0.5">
          {new Date(tooltip.date).toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "long" })}
        </p>
        <p className="text-[var(--color-muted-foreground)]">
          {tooltip.hour}:00 ~ {tooltip.hour}:59
        </p>

        {/* TX 수 + 블록 수 */}
        <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-[var(--color-border)]">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tooltip.count === 0 ? "var(--color-muted-foreground)" : C.primary }} />
            <span className="font-bold" style={{ color: tooltip.count === 0 ? "var(--color-muted-foreground)" : C.primary }}>
              {tooltip.count.toLocaleString()} TX
            </span>
          </div>
          {tooltip.blockCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.success }} />
              <span className="font-bold" style={{ color: C.success }}>
                {tooltip.blockCount.toLocaleString()} 블록
              </span>
            </div>
          )}
        </div>

        {/* 통계 정보 */}
        <div className="mt-1.5 pt-1.5 border-t border-[var(--color-border)] space-y-0.5">
          {tooltip.isDailyPeak && (
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${C.warning}22`, color: C.warning }}>
                ★ 당일 피크
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-[var(--color-muted-foreground)]">전체 평균 대비</span>
            <span className="font-semibold tabular-nums" style={{
              color: tooltip.avgRatio >= 1.5 ? C.danger : tooltip.avgRatio >= 1 ? C.warning : "var(--color-muted-foreground)"
            }}>
              {tooltip.avgRatio > 0 ? `${tooltip.avgRatio.toFixed(1)}x` : "—"}
              {tooltip.avgRatio >= 2 && " ↑↑"}
              {tooltip.avgRatio >= 1.5 && tooltip.avgRatio < 2 && " ↑"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-[var(--color-muted-foreground)]">{tooltip.hour}시 7일 평균</span>
            <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">{tooltip.hourAvg.toFixed(1)} TX</span>
          </div>
          {tooltip.prevDayCount !== null && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--color-muted-foreground)]">전일 동시간</span>
              <span className="font-semibold tabular-nums" style={{
                color: tooltip.count > tooltip.prevDayCount ? C.success : tooltip.count < tooltip.prevDayCount ? C.danger : "var(--color-muted-foreground)"
              }}>
                {tooltip.prevDayCount.toLocaleString()} TX
                {tooltip.count !== tooltip.prevDayCount && (
                  <span className="ml-1">
                    ({tooltip.count > tooltip.prevDayCount ? "+" : ""}{tooltip.count - tooltip.prevDayCount})
                  </span>
                )}
              </span>
            </div>
          )}
          {tooltip.txPerBlock !== null && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--color-muted-foreground)]">블록당 TX</span>
              <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">{tooltip.txPerBlock.toFixed(1)}</span>
            </div>
          )}
          {tooltip.dailyTotal > 0 && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--color-muted-foreground)]">당일 비중</span>
              <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">
                {tooltip.dailyShare.toFixed(1)}%
                <span className="text-[var(--color-muted-foreground)] font-normal ml-1">
                  ({tooltip.count}/{tooltip.dailyTotal})
                </span>
              </span>
            </div>
          )}
        </div>

        {/* 체인코드별 분류 */}
        {tooltip.chaincodes.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-[var(--color-border)]">
            <p className="text-[10px] text-[var(--color-muted-foreground)] mb-1">체인코드</p>
            {tooltip.chaincodes.slice(0, 3).map((cc, i) => (
              <div key={cc.name} className="flex items-center justify-between gap-3 text-[11px]">
                <span className="flex items-center gap-1.5 truncate max-w-[120px]">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-[var(--color-muted-foreground)]">{cc.name}</span>
                </span>
                <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">{cc.count.toLocaleString()}</span>
              </div>
            ))}
            {tooltip.chaincodes.length > 3 && (
              <p className="text-[10px] text-[var(--color-muted-foreground)] mt-0.5">+{tooltip.chaincodes.length - 3}개 더</p>
            )}
          </div>
        )}

        {/* MSP(조직)별 분류 */}
        {tooltip.msps.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-[var(--color-border)]">
            <p className="text-[10px] text-[var(--color-muted-foreground)] mb-1">조직 (MSP)</p>
            {tooltip.msps.slice(0, 3).map((m, i) => (
              <div key={m.name} className="flex items-center justify-between gap-3 text-[11px]">
                <span className="flex items-center gap-1.5 truncate max-w-[120px]">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[(i + 3) % PIE_COLORS.length] }} />
                  <span className="text-[var(--color-muted-foreground)]">{m.name}</span>
                </span>
                <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">{m.count.toLocaleString()}</span>
              </div>
            ))}
            {tooltip.msps.length > 3 && (
              <p className="text-[10px] text-[var(--color-muted-foreground)] mt-0.5">+{tooltip.msps.length - 3}개 더</p>
            )}
          </div>
        )}
      </div>
      {/* 아래쪽 화살표 */}
      <div className="w-2 h-2 rotate-45 mx-auto -mt-1" style={{ backgroundColor: "var(--color-card)", borderRight: "1px solid var(--color-border)", borderBottom: "1px solid var(--color-border)" }} />
    </div>,
    document.body
  );

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px] heatmap-container relative">
        {/* Portal 기반 툴팁 */}
        {tooltipPortal}

        {/* Hour labels */}
        <div className="flex gap-[2px] mb-1 ml-20">
          {hours.map(h => (
            <div key={h} className="flex-1 text-center text-[9px] text-[var(--color-muted-foreground)]">
              {h % 3 === 0 ? `${h}시` : ""}
            </div>
          ))}
        </div>

        {/* Grid rows */}
        {grid.dates.map(date => (
          <div key={date} className="flex items-center gap-[2px] mb-[2px]">
            <div className="w-20 text-right pr-2 text-[10px] text-[var(--color-muted-foreground)] flex-shrink-0">
              {new Date(date).toLocaleDateString("ko-KR", { month: "short", day: "numeric", weekday: "short" })}
            </div>
            {hours.map(h => {
              const val = grid.map.get(`${date}_${h}`) || 0;
              const intensity = val / grid.maxVal;
              const bg = val === 0
                ? "var(--color-muted)"
                : `rgba(129, 140, 248, ${0.12 + intensity * 0.88})`;
              return (
                <div key={h}
                  className="flex-1 aspect-square rounded-[3px] cursor-default transition-all hover:scale-[1.4] hover:z-10 hover:ring-2 hover:ring-[var(--color-primary)]/50 hover:ring-offset-1"
                  style={{ backgroundColor: bg, minHeight: "14px" }}
                  onMouseEnter={(e) => handleMouseEnter(e, date, h, val)}
                  onMouseLeave={() => setTooltip(null)}
                />
              );
            })}
          </div>
        ))}

        {/* Legend + Peak info */}
        <div className="flex items-center justify-between mt-3 ml-20">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[var(--color-muted-foreground)]">적음</span>
            {[0.12, 0.3, 0.5, 0.7, 1].map((op, i) => (
              <div key={i} className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: `rgba(129, 140, 248, ${op})` }} />
            ))}
            <span className="text-[10px] text-[var(--color-muted-foreground)]">많음</span>
          </div>
          {grid.peakHour && (
            <span className="text-[10px] text-[var(--color-muted-foreground)]">
              피크: <strong className="text-[var(--color-card-foreground)]">{grid.peakHour[0]}시</strong> ({grid.peakHour[1].toLocaleString()}건)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty() {
  return <div className="h-full flex items-center justify-center text-sm text-[var(--color-muted-foreground)]">데이터가 없습니다</div>;
}

/* ── 공통 axis 속성 ── */
const axTick = { fontSize: 11, fill: "var(--color-muted-foreground)" };
const axSmall = { fontSize: 10, fill: "var(--color-muted-foreground)" };
const gridProps = { strokeDasharray: "3 3" as const, stroke: "var(--color-border)", opacity: 0.4 };

/* ══════════════════════════════════════════
   메인 컴포넌트
══════════════════════════════════════════ */
export function DashboardCharts() {
  const [activity, setActivity] = useState<BlockActivity[]>([]);
  const [timeline, setTimeline] = useState<BlockTimeline[]>([]);
  const [txByOrg, setTxByOrg] = useState<TxByOrg[]>([]);
  const [ccActivity, setCcActivity] = useState<ChaincodeActivity[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapRow[]>([]);
  const [heatmapChaincode, setHeatmapChaincode] = useState<HeatmapChaincodeRow[]>([]);
  const [heatmapBlocks, setHeatmapBlocks] = useState<HeatmapBlockRow[]>([]);
  const [heatmapMsp, setHeatmapMsp] = useState<HeatmapMspRow[]>([]);
  const [blockSizeDist, setBlockSizeDist] = useState<BlockSizeDist[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [actRes, tlRes, orgRes, ccRes, hmRes, bsRes] = await Promise.all([
        fetch("/api/block-activity?days=14"),
        fetch("/api/block-timeline?limit=30"),
        fetch("/api/tx-by-org"),
        fetch("/api/chaincode-activity"),
        fetch("/api/tx-heatmap"),
        fetch("/api/block-size-distribution"),
      ]);
      const results = await Promise.all([actRes.json(), tlRes.json(), orgRes.json(), ccRes.json(), hmRes.json(), bsRes.json()]);
      const [actD, tlD, orgD, ccD, hmD, bsD] = results;
      if (Array.isArray(actD)) setActivity(actD);
      if (Array.isArray(tlD)) setTimeline(tlD);
      if (Array.isArray(orgD)) setTxByOrg(orgD);
      if (Array.isArray(ccD)) setCcActivity(ccD);
      // tx-heatmap API returns { base, byChaincode, blockCounts, byMsp }
      if (hmD && Array.isArray(hmD.base)) {
        setHeatmap(hmD.base);
        if (Array.isArray(hmD.byChaincode)) setHeatmapChaincode(hmD.byChaincode);
        if (Array.isArray(hmD.blockCounts)) setHeatmapBlocks(hmD.blockCounts);
        if (Array.isArray(hmD.byMsp)) setHeatmapMsp(hmD.byMsp);
      } else if (Array.isArray(hmD)) {
        setHeatmap(hmD);
      }
      if (Array.isArray(bsD)) setBlockSizeDist(bsD);
      setLastRefresh(new Date());
    } catch (e) { console.error("Chart data fetch error:", e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchData]);

  /* ── 파생 통계 ── */
  const totalTx = timeline.reduce((s, t) => s + Number(t.tx_count), 0);

  // 활성 구간만 추출 (60초 이내 간격 = 실제 TX 처리 중인 구간)
  const ACTIVE_THRESHOLD = 60;
  const activeIntervals = timeline
    .map(t => t.interval_sec !== null ? Number(t.interval_sec) : null)
    .filter((v): v is number => v !== null && !isNaN(v) && v > 0 && v <= ACTIVE_THRESHOLD);

  // ① 블록 생성 주기: 활성 구간에서 블록이 몇 초에 한 번씩 만들어지는지
  const blockInterval = activeIntervals.length > 0
    ? activeIntervals.reduce((s, v) => s + v, 0) / activeIntervals.length : null;

  // ② TX 확정 시간: 마지막 블록 기준 — 직전 블록과의 간격이 곧 TX 대기 시간
  //    (HLF는 TX가 들어오면 BatchTimeout 또는 BatchSize에 의해 블록이 커밋됨)
  const lastInterval = timeline.length > 0
    ? Number(timeline[timeline.length - 1].interval_sec ?? 0) : null;
  const txFinalityTime = lastInterval && lastInterval > 0 && lastInterval <= ACTIVE_THRESHOLD
    ? lastInterval : blockInterval; // fallback to 평균

  // ③ TPS: 활성 구간(burst) 기반
  type Burst = { txSum: number; durationSec: number; blocks: number };
  const bursts: Burst[] = [];
  let curBurst: Burst = { txSum: 0, durationSec: 0, blocks: 0 };

  timeline.forEach(t => {
    const iv = t.interval_sec !== null ? Number(t.interval_sec) : null;
    const txc = Number(t.tx_count);
    if (iv !== null && iv > 0 && iv <= ACTIVE_THRESHOLD) {
      curBurst.txSum += txc;
      curBurst.durationSec += iv;
      curBurst.blocks += 1;
    } else {
      if (curBurst.blocks >= 2 && curBurst.durationSec > 0) bursts.push({ ...curBurst });
      curBurst = { txSum: txc, durationSec: 0, blocks: 1 };
    }
  });
  if (curBurst.blocks >= 2 && curBurst.durationSec > 0) bursts.push({ ...curBurst });

  const burstTps = bursts.map(b => b.txSum / b.durationSec);
  const peakTPS = burstTps.length > 0 ? Math.max(...burstTps) : null;
  const avgTPS = burstTps.length > 0
    ? burstTps.reduce((s, v) => s + v, 0) / burstTps.length : null;

  // 포매터
  function fmtDuration(sec: number): string {
    if (sec < 1) return `${(sec * 1000).toFixed(0)}ms`;
    if (sec < 60) return `${sec.toFixed(1)}초`;
    if (sec < 3600) return `${(sec / 60).toFixed(1)}분`;
    return `${(sec / 3600).toFixed(1)}시간`;
  }
  function fmtTPS(tps: number): string {
    if (tps >= 100) return `${Math.round(tps)} TPS`;
    if (tps >= 1) return `${tps.toFixed(1)} TPS`;
    if (tps >= 0.01) return `${(tps * 60).toFixed(1)} TX/min`;
    return `${(tps * 3600).toFixed(1)} TX/hr`;
  }

  /* 차트 데이터 변환 */
  const actChart = activity.map(d => ({
    date: new Date(d.date).toLocaleDateString("ko-KR", { month: "short", day: "numeric" }),
    blocks: parseInt(d.block_count), txs: parseInt(d.tx_count),
  }));
  const orgChart = txByOrg.map(d => ({ name: d.name, value: parseInt(d.value) }));
  const orgTotal = orgChart.reduce((s, d) => s + d.value, 0);
  const tlChart = timeline.map(d => ({
    block: `#${d.block_num}`,
    interval: parseFloat(Number(d.interval_sec ?? 0).toFixed(2)),
    txs: Number(d.tx_count),
  }));
  const ccChart = ccActivity.map(d => ({
    name: d.fn ? `${d.chaincode_name}::${d.fn}` : d.chaincode_name,
    count: parseInt(d.call_count),
  }));
  const bsChart = blockSizeDist.map(d => ({
    txCount: `${d.tx_count} TX`, blocks: parseInt(d.block_count),
  }));

  /* 로딩 */
  if (loading) return (
    <div className="space-y-6">
      <div className="bg-[var(--color-card)] rounded-2xl border border-[var(--color-border)] p-4 h-14 animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className={`bg-[var(--color-card)] rounded-2xl border border-[var(--color-border)] p-6 h-80 animate-pulse ${i === 1 ? "lg:col-span-2" : ""}`}>
            <div className="h-4 bg-[var(--color-muted)] rounded w-1/3 mb-4" />
            <div className="h-56 bg-[var(--color-muted)] rounded" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* ─── 통계 바 ─── */}
      <div className="bg-[var(--color-card)] rounded-2xl border border-[var(--color-border)] p-4 shadow-sm animate-fade-in-up">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 justify-between">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {blockInterval !== null && (
              <Stat icon={<Boxes className="w-3.5 h-3.5" />} iconBg="bg-[var(--color-primary)]/10" iconColor="text-[var(--color-primary)]"
                label="블록 생성 주기" value={`${fmtDuration(blockInterval)}마다`} />
            )}
            {txFinalityTime !== null && (
              <Stat icon={<Clock className="w-3.5 h-3.5" />} iconBg="bg-[var(--color-cyan)]/10" iconColor="text-[var(--color-cyan)]"
                label="TX 확정 시간" value={fmtDuration(txFinalityTime)} />
            )}
            <div className="h-5 w-px bg-[var(--color-border)]" />
            {peakTPS !== null && (
              <Stat icon={<Gauge className="w-3.5 h-3.5" />} iconBg="bg-[var(--color-warning)]/10" iconColor="text-[var(--color-warning)]"
                label="피크 TPS" value={fmtTPS(peakTPS)} />
            )}
            {avgTPS !== null && (
              <Stat icon={<Gauge className="w-3.5 h-3.5" />} iconBg="bg-[var(--color-success)]/10" iconColor="text-[var(--color-success)]"
                label="평균 TPS" value={fmtTPS(avgTPS)} />
            )}
            <span className="text-[10px] text-[var(--color-muted-foreground)] bg-[var(--color-muted)] px-2 py-1 rounded-full tabular-nums">
              {totalTx.toLocaleString()} TX · {timeline.length} 블록
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all font-medium ${
                autoRefresh
                  ? "bg-[var(--color-success)]/10 border-[var(--color-success)]/30 text-[var(--color-success)]"
                  : "bg-[var(--color-muted)] border-[var(--color-border)] text-[var(--color-muted-foreground)]"
              }`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${autoRefresh ? "bg-[var(--color-success)] animate-pulse" : "bg-[var(--color-muted-foreground)]"}`} />
              {autoRefresh ? "LIVE" : "정지"}
            </button>
            <button onClick={fetchData} disabled={refreshing}
              className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:text-[var(--color-card-foreground)] hover:border-[var(--color-primary)] transition-colors disabled:opacity-50">
              <RefreshCcw className={`w-3 h-3 inline mr-1 ${refreshing ? "animate-spin" : ""}`} />새로고침
            </button>
            <span className="text-xs text-[var(--color-muted-foreground)] tabular-nums">{lastRefresh.toLocaleTimeString("ko-KR")}</span>
          </div>
        </div>
      </div>

      {/* ─── Row 1: 일별 추이 + 조직별 분포 ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard className="lg:col-span-2" title="일별 블록 & 트랜잭션 추이" badge="최근 14일">
          <div className="h-64">
            {actChart.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={actChart}>
                  <defs>
                    <linearGradient id="gB" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.primary} stopOpacity={0.2} /><stop offset="95%" stopColor={C.primary} stopOpacity={0} /></linearGradient>
                    <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.success} stopOpacity={0.2} /><stop offset="95%" stopColor={C.success} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" tick={axSmall} axisLine={false} tickLine={false} />
                  <YAxis tick={axTick} axisLine={false} tickLine={false} />
                  <Tooltip content={<TipActivity />} />
                  <Area type="monotone" dataKey="blocks" name="블록" stroke={C.primary} fill="url(#gB)" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C.primary }} />
                  <Area type="monotone" dataKey="txs" name="트랜잭션" stroke={C.success} fill="url(#gT)" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C.success }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard title="조직별 트랜잭션 분포">
          {orgChart.length === 0 ? <div className="h-64"><Empty /></div> : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={orgChart} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value" stroke="none">
                      {orgChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<TipOrg />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {orgChart.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-[var(--color-muted-foreground)] truncate max-w-[120px]">{d.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[var(--color-card-foreground)] tabular-nums">{d.value.toLocaleString()}</span>
                      <span className="text-[var(--color-muted-foreground)] w-10 text-right">({orgTotal > 0 ? ((d.value / orgTotal) * 100).toFixed(0) : 0}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>
      </div>

      {/* ─── Row 2: 블록 타임라인 ─── */}
      <ChartCard title="블록 생성 간격 & 트랜잭션 수" badge={`최근 ${tlChart.length}개 블록`}>
        <div className="h-56">
          {tlChart.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={tlChart}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="block" tick={axSmall} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(tlChart.length / 10) - 1)} />
                <YAxis yAxisId="left" tick={axTick} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={axTick} axisLine={false} tickLine={false} />
                <Tooltip content={<TipTimeline />} />
                <Bar yAxisId="left" dataKey="interval" name="생성 간격" fill={C.primary} fillOpacity={0.6} radius={[3, 3, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="txs" name="TX 수" stroke={C.success} strokeWidth={2} dot={{ r: 2.5, fill: C.success, stroke: "none" }} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>

      {/* ─── Row 3: 히트맵 + 블록 크기 분포 ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard className="lg:col-span-2" title="시간대별 트랜잭션 히트맵" badge="최근 7일">
          <HeatmapGrid data={heatmap} chaincodeData={heatmapChaincode} blockData={heatmapBlocks} mspData={heatmapMsp} />
        </ChartCard>

        <ChartCard title="블록당 TX 수 분포">
          <div className="h-56">
            {bsChart.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bsChart}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="txCount" tick={axSmall} axisLine={false} tickLine={false} />
                  <YAxis tick={axTick} axisLine={false} tickLine={false} />
                  <Tooltip content={<TipGeneric />} />
                  <Bar dataKey="blocks" name="블록 수" fill={C.accent} fillOpacity={0.7} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>
      </div>

      {/* ─── Row 4: 체인코드 호출 빈도 ─── */}
      {ccChart.length > 0 && (
        <ChartCard
          title={<span className="flex items-center gap-2"><BarChart3 className="w-4 h-4" style={{ color: C.accent }} /> 체인코드 함수별 호출 빈도</span>}
          badge={`상위 ${ccChart.length}개`}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ccChart} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid {...gridProps} horizontal={false} />
                <XAxis type="number" tick={axTick} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={axSmall} width={160} axisLine={false} tickLine={false} />
                <Tooltip content={<TipGeneric />} />
                <Bar dataKey="count" name="호출 수" radius={[0, 4, 4, 0]}>
                  {ccChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={0.75} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}
    </div>
  );
}

/* ── 재사용 컴포넌트 ── */
function ChartCard({ children, title, badge, className = "" }: {
  children: React.ReactNode; title: React.ReactNode; badge?: string; className?: string;
}) {
  return (
    <div className={`bg-[var(--color-card)] rounded-2xl border border-[var(--color-border)] p-5 shadow-sm animate-fade-in-up ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--color-card-foreground)]">{title}</h3>
        {badge && <span className="text-xs text-[var(--color-muted-foreground)]">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Stat({ icon, iconBg, iconColor, label, value }: {
  icon: React.ReactNode; iconBg: string; iconColor: string; label: string; value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className={`p-1.5 rounded-lg ${iconBg} ${iconColor}`}>{icon}</div>
      <span className="text-[var(--color-muted-foreground)]">{label}</span>
      <span className="font-bold text-[var(--color-card-foreground)]">{value}</span>
    </div>
  );
}
